<script setup lang="ts">
import { user } from '@ideaflow/ui/classes/user'
import { computed, onBeforeUnmount, onMounted, shallowRef, useTemplateRef, watch } from 'vue'
import type { UserInfo } from '@/stores/user'
import {
  isAllowedLegacyPlayerBridgeParentOrigin,
  isLegacyPlayerBridgeAuthMessage,
  LEGACY_PLAYER_BRIDGE_AUTH_MESSAGE_TYPE,
  LEGACY_PLAYER_BRIDGE_AUTH_REQUEST,
  LEGACY_PLAYER_BRIDGE_AUTH_RESPONSE,
} from '@/utils/legacy-player'

defineOptions({
  name: 'LegacyPlayerBridgeFrame',
})

const PARENT_USER_INFO_REQUEST_TIMEOUT_MS = 1200

const props = defineProps<{
  url: string | null
  loadKey: number
}>()

const isLoading = shallowRef(false)
const showLoadHint = shallowRef(false)
const parentUserInfo = shallowRef<UserInfo | null>(null)
const frameRef = useTemplateRef<HTMLIFrameElement>('frame')
let loadTimer: number | null = null
let parentUserInfoTimer: number | null = null
let parentUserInfoPromise: Promise<UserInfo | null> | null = null
let parentUserInfoResolve: ((userInfo: UserInfo | null) => void) | null = null
let parentUserInfoRequestId: string | null = null

const frameTitle = computed(() => {
  if (!props.url) {
    return '内容播放器'
  }

  try {
    const parsed = new URL(props.url)

    if (parsed.pathname === '/radio.html') {
      return '听听播放器'
    }

    if (parsed.pathname === '/rolematerial.html') {
      return '内容播放器'
    }

    return '互动内容播放器'
  } catch {
    return '内容播放器'
  }
})

const playerOrigin = computed(() => {
  if (!props.url) {
    return null
  }

  try {
    return new URL(props.url).origin
  } catch {
    return null
  }
})

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function normalizeUserInfo(value: unknown): UserInfo | null {
  if (!isRecord(value)) {
    return null
  }

  const userInfo = JSON.parse(JSON.stringify(value)) as UserInfo

  if (!userInfo.id && !userInfo.uid) {
    return null
  }

  return userInfo
}

function getCookieUserInfo(): UserInfo | null {
  return normalizeUserInfo(user.get())
}

function getKnownBridgeUserInfo(): UserInfo | null {
  return getCookieUserInfo() ?? parentUserInfo.value
}

function getParentUserInfoRequestTargetOrigin(): string {
  if (typeof document === 'undefined' || !document.referrer) {
    return '*'
  }

  try {
    const referrerOrigin = new URL(document.referrer).origin

    if (isAllowedLegacyPlayerBridgeParentOrigin(referrerOrigin)) {
      return referrerOrigin
    }
  } catch {
    // The request contains no user data; a wildcard keeps the fallback usable without a referrer.
  }

  return '*'
}

function completeParentUserInfoRequest(userInfo: UserInfo | null) {
  if (parentUserInfoTimer !== null) {
    window.clearTimeout(parentUserInfoTimer)
    parentUserInfoTimer = null
  }

  const resolve = parentUserInfoResolve
  parentUserInfoResolve = null
  parentUserInfoPromise = null
  parentUserInfoRequestId = null
  resolve?.(userInfo)
}

function requestParentUserInfo(): Promise<UserInfo | null> {
  const knownUserInfo = getKnownBridgeUserInfo()

  if (knownUserInfo) {
    return Promise.resolve(knownUserInfo)
  }

  if (typeof window === 'undefined' || window.parent === window) {
    return Promise.resolve(null)
  }

  if (parentUserInfoPromise) {
    return parentUserInfoPromise
  }

  parentUserInfoPromise = new Promise((resolve) => {
    const requestId = `legacy-player-auth-${Date.now()}-${Math.random().toString(36).slice(2)}`
    parentUserInfoRequestId = requestId
    parentUserInfoResolve = resolve

    window.parent.postMessage(
      {
        type: LEGACY_PLAYER_BRIDGE_AUTH_MESSAGE_TYPE,
        id: requestId,
        action: LEGACY_PLAYER_BRIDGE_AUTH_REQUEST,
      },
      getParentUserInfoRequestTargetOrigin(),
    )

    parentUserInfoTimer = window.setTimeout(() => {
      completeParentUserInfoRequest(null)
    }, PARENT_USER_INFO_REQUEST_TIMEOUT_MS)
  })

  return parentUserInfoPromise
}

function getSafeAreaInsets() {
  const style = window.getComputedStyle(document.documentElement)

  return {
    top: style.getPropertyValue('--safe-top'),
    bottom: style.getPropertyValue('--safe-bottom'),
  }
}

function postToPlayer(message: unknown) {
  const targetWindow = frameRef.value?.contentWindow
  const targetOrigin = playerOrigin.value

  if (!targetWindow || !targetOrigin) {
    return
  }

  targetWindow.postMessage(message, targetOrigin)
}

async function resolveBridgeUserInfo(): Promise<UserInfo | null> {
  return getKnownBridgeUserInfo() ?? (await requestParentUserInfo())
}

async function resolveBridgeRequest(name: unknown): Promise<unknown> {
  if (name === 'getLocationOrigin') {
    return window.location.origin
  }

  if (name === 'getSafeArea') {
    return getSafeAreaInsets()
  }

  if (name === 'getUserInfo' || name === 'userInfo') {
    return resolveBridgeUserInfo()
  }

  return undefined
}

function handleParentAuthMessage(event: MessageEvent): boolean {
  if (
    event.source !== window.parent ||
    !isAllowedLegacyPlayerBridgeParentOrigin(event.origin) ||
    !isLegacyPlayerBridgeAuthMessage(event.data) ||
    event.data.action !== LEGACY_PLAYER_BRIDGE_AUTH_RESPONSE
  ) {
    return false
  }

  const userInfo = normalizeUserInfo(event.data.data)

  if (userInfo) {
    parentUserInfo.value = userInfo
  }

  if (event.data.id === parentUserInfoRequestId) {
    completeParentUserInfoRequest(userInfo)
  }

  return true
}

async function handleMessage(event: MessageEvent) {
  if (handleParentAuthMessage(event)) {
    return
  }

  if (
    !frameRef.value?.contentWindow ||
    event.source !== frameRef.value.contentWindow ||
    event.origin !== playerOrigin.value ||
    !isRecord(event.data)
  ) {
    return
  }

  if (event.data.type === 'options') {
    postToPlayer({
      id: event.data.id,
      type: 'response',
      code: 0,
      msg: '',
      data: window.location.origin,
    })
    return
  }

  if (event.data.type === 'request') {
    const responseData = await resolveBridgeRequest(event.data.name)

    postToPlayer({
      id: event.data.id,
      type: 'response',
      code: responseData === undefined ? 1 : 0,
      msg: responseData === undefined ? `接口 ${String(event.data.name)} 不存在` : '',
      data: responseData ?? null,
    })
    return
  }

  const legacyPayload = isRecord(event.data.data) ? event.data.data : null
  const legacyResponseData = await resolveBridgeRequest(legacyPayload?.fn)

  if (legacyResponseData === undefined) {
    return
  }

  postToPlayer({
    id: event.data.id,
    data: legacyResponseData,
  })
}

function clearLoadTimer() {
  if (loadTimer === null) {
    return
  }

  window.clearTimeout(loadTimer)
  loadTimer = null
}

function startLoading() {
  clearLoadTimer()
  showLoadHint.value = false
  isLoading.value = Boolean(props.url)

  if (!isLoading.value) {
    return
  }

  loadTimer = window.setTimeout(() => {
    showLoadHint.value = true
  }, 9000)
}

function handleLoaded() {
  clearLoadTimer()
  isLoading.value = false
  showLoadHint.value = false
}

onBeforeUnmount(() => {
  clearLoadTimer()
  completeParentUserInfoRequest(null)
  window.removeEventListener('message', handleMessage)
})

onMounted(() => {
  window.addEventListener('message', handleMessage)
  void requestParentUserInfo()
})

watch(
  () => [props.url, props.loadKey] as const,
  () => {
    startLoading()
  },
  { immediate: true },
)
</script>

<template>
  <main class="legacy-player-bridge" :aria-busy="isLoading">
    <div v-if="isLoading" class="legacy-player-bridge__loading" aria-live="polite">
      <span class="legacy-player-bridge__spinner" aria-hidden="true" />
      <span v-if="showLoadHint">播放器加载较慢，可返回原页面重新打开</span>
    </div>

    <iframe
      v-if="url"
      :key="loadKey"
      ref="frame"
      class="legacy-player-bridge__frame"
      :src="url"
      :title="frameTitle"
      allow="autoplay; clipboard-write; fullscreen; picture-in-picture"
      sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox allow-downloads allow-modals"
      referrerpolicy="strict-origin-when-cross-origin"
      @load="handleLoaded"
    />

    <p v-else class="legacy-player-bridge__fallback">播放器链接无效</p>
  </main>
</template>

<style scoped>
.legacy-player-bridge {
  position: fixed;
  inset: 0;
  overflow: hidden;
  width: 100vw;
  height: 100dvh;
  background: #000;
  color: #fff;
}

.legacy-player-bridge__frame {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border: 0;
  background: #000;
}

.legacy-player-bridge__loading,
.legacy-player-bridge__fallback {
  position: absolute;
  inset: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin: 0;
  background: #050506;
  color: rgba(255, 255, 255, 0.72);
  font-size: 13px;
}

.legacy-player-bridge__spinner {
  width: 24px;
  height: 24px;
  border: 2px solid rgba(255, 255, 255, 0.18);
  border-top-color: rgba(255, 255, 255, 0.82);
  border-radius: 999px;
  animation: legacy-player-bridge-spin 0.8s linear infinite;
}

@keyframes legacy-player-bridge-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
